import React, { useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import logoBlack from '../assets/Proteus_Black-01.svg';
import logoWhite from '../assets/Proteus_White-01.svg';
import { Pill, PillArrow } from './Pill';
import { useScrolled } from '../hooks/useScrolled';
import { siteConfig, type HeroVariant } from '../siteConfig';

const LOGO_HEIGHT = 26;
const LOGO_WIDTH = Math.round((LOGO_HEIGHT * 1000) / 150);

const Bar = styled.nav<{ $solid: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px ${({ theme }) => theme.container.gutter};
  border-bottom: 1px solid transparent;
  transition: background 0.4s ease, border-color 0.4s ease, box-shadow 0.4s ease;

  ${({ $solid, theme }) =>
    $solid &&
    css`
      background: ${theme.colors.paper};
      backdrop-filter: blur(18px);
      border-bottom-color: ${theme.colors.line};
      box-shadow: 0 10px 40px -28px rgba(11, 21, 36, 0.5);
    `}
`;

const LogoLink = styled.a`
  display: flex;
  align-items: center;
`;

const Links = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(14px, 2.4vw, 38px);
  font-size: 0.94rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const NavLink = styled.a<{ $onDark: boolean }>`
  color: ${({ $onDark, theme }) => ($onDark ? theme.colors.paper : theme.colors.ink)};
  font-weight: 500;

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const Burger = styled.button<{ $open: boolean; $onDark: boolean }>`
  display: none;
  z-index: 101;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 44px;
  height: 44px;
  margin-right: -10px;
  padding: 0;
  background: none;
  border: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
  }

  span {
    display: block;
    width: 24px;
    height: 2px;
    border-radius: 2px;
    background: ${({ $onDark, theme }) => ($onDark ? theme.colors.paper : theme.colors.ink)};
    transform-origin: center;
    transition: transform 0.3s ease, opacity 0.2s ease, background 0.25s ease;
  }

  ${({ $open }) =>
    $open &&
    css`
      span:nth-child(1) {
        transform: translateY(7px) rotate(45deg);
      }
      span:nth-child(2) {
        opacity: 0;
      }
      span:nth-child(3) {
        transform: translateY(-7px) rotate(-45deg);
      }
    `}
`;

const Panel = styled.div<{ $open: boolean }>`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: fixed;
    inset: 0;
    z-index: 90;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
    padding: 0 ${({ theme }) => theme.container.gutter};
    background: ${({ theme }) => theme.colors.ink};
    color: ${({ theme }) => theme.colors.paper};
    opacity: 0;
    visibility: hidden;
    transform: translateY(-8px);
    transition: opacity 0.3s ease, transform 0.3s ease, visibility 0.3s;

    ${({ $open }) =>
      $open &&
      css`
        opacity: 1;
        visibility: visible;
        transform: none;
      `}
  }
`;

const PanelLink = styled.a`
  color: ${({ theme }) => theme.colors.paper};
  font-size: clamp(1.8rem, 8vw, 2.6rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  padding: 10px 0;

  &:hover {
    color: ${({ theme }) => theme.colors.sky};
  }
`;

const PanelCta = styled(Pill)`
  align-self: flex-start;
  margin-top: 28px;
`;

const PanelMeta = styled.div`
  margin-top: 28px;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.78rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.onInk.muted};
`;

const sections = [
  { href: '/#services', label: 'Services' },
  { href: '/#industries', label: 'Industries' },
  { href: '/#about', label: 'About' }
];

interface NavbarProps {
  /** Omitted on pages that have no hero for the transparent navbar to sit over. */
  heroVariant?: HeroVariant;
}

const Navbar: React.FC<NavbarProps> = ({ heroVariant }) => {
  const scrolled = useScrolled(24);
  const [menuOpen, setMenuOpen] = useState(false);

  const close = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (!menuOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen, close]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;

    const mq = window.matchMedia('(min-width: 769px)');
    const onChange = () => {
      if (mq.matches) close();
    };

    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [close]);

  // The kinetic hero is dark, so the transparent navbar sits on ink until it solidifies.
  const overDarkHero = (heroVariant === 'kinetic' && !scrolled) || menuOpen;
  const solid = scrolled && !menuOpen;

  return (
    <>
      <Bar $solid={solid} aria-label="Primary">
        <LogoLink href="/#top" onClick={close}>
          <img
            src={overDarkHero ? logoWhite : logoBlack}
            alt="Proteus Technologies"
            width={LOGO_WIDTH}
            height={LOGO_HEIGHT}
            style={{ height: LOGO_HEIGHT, width: 'auto' }}
            loading="eager"
          />
        </LogoLink>

        <Links>
          {sections.map(({ href, label }) => (
            <NavLink key={href} href={href} $onDark={overDarkHero}>
              {label}
            </NavLink>
          ))}
          <Pill
            href="/#contact"
            $variant={overDarkHero ? 'paper' : 'ink'}
            $size="sm"
          >
            Get in touch <PillArrow>&rarr;</PillArrow>
          </Pill>
        </Links>

        <Burger
          type="button"
          $open={menuOpen}
          $onDark={overDarkHero}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
          onClick={() => setMenuOpen(open => !open)}
        >
          <span />
          <span />
          <span />
        </Burger>
      </Bar>

      <Panel id="mobile-menu" $open={menuOpen} aria-hidden={!menuOpen}>
        {sections.map(({ href, label }) => (
          <PanelLink key={href} href={href} onClick={close}>
            {label}
          </PanelLink>
        ))}
        <PanelCta href="/#contact" $variant="paper" $size="md" onClick={close}>
          Get in touch <PillArrow>&rarr;</PillArrow>
        </PanelCta>
        <PanelMeta>{siteConfig.email}</PanelMeta>
      </Panel>
    </>
  );
};

export default Navbar;
